import React from "react";
import { FiCheckCircle, FiPhone, FiLink, FiArrowUpRight } from "react-icons/fi";

const ResponsibleGambling = () => {
  const tips = [
    { title: "Set a Budget", body: "Only gamble with money you can afford to lose. Never use money meant for essentials like rent, food, or bills." },
    { title: "Set Time Limits", body: "Decide how long you’ll play before you start and stick to it. Use alarms or reminders to help you stay on track." },
    { title: "Take Regular Breaks", body: "Step away from gambling regularly to maintain perspective and avoid getting caught up in the moment." },
    { title: "Don’t Chase Losses", body: "Accept losses as part of the game. Trying to win back money often leads to bigger losses." },
    { title: "Keep it Social", body: "Gambling should be a social activity. Avoid gambling alone or in isolation." },
    { title: "Stay Sober", body: "Don’t gamble when under the influence of alcohol or drugs, as this impairs judgment." },
  ];

  const resources = [
    {
      name: "National Problem Gambling Helpline",
      blurb: "Free, confidential support available 24/7 for problem gamblers and their families.",
      phone: "1-800-522-4700",
      href: "https://www.ncpgambling.org/",
    },
    {
      name: "Gamblers Anonymous",
      blurb: "A fellowship of people who share their experience to help others recover from gambling addiction.",
      phone: "1-855-222-5542",
      href: "https://www.gamblersanonymous.org/ga/",
    },
    {
      name: "GamCare",
      blurb: "Leading provider of information, advice, support and free counseling for gambling problems.",
      phone: "0808 8020 133",
      href: "https://www.gamcare.org.uk/",
    },
  ];

  return (
    <section className="bg-gray-50 text-gray-900 dark:bg-[#121212] dark:text-gray-100 transition-colors duration-300 py-16 sm:py-24">
      <div className="w-full mx-auto max-w-[1000px] px-6">
        {/* HERO — centered */}
        <div className="text-center max-w-2xl mx-auto">
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">
            Responsible Gambling
          </h1>
          <p className="mt-3 text-gray-600 dark:text-gray-300 leading-relaxed">
            Your safety and wellbeing are our top priority. We&apos;re committed to
            providing a safe, secure, and responsible gaming environment for all
            our players.
          </p>
        </div>

        {/* Recognize the Warning Signs */}
        <div className="mt-16 border-t border-gray-200 dark:border-gray-700 pt-6">
          <h2 className="text-xl sm:text-2xl font-semibold text-left">
            Recognize the Warning Signs
          </h2>
          <div className="my-8 h-px bg-gray-200 dark:bg-gray-700" />
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 text-left">
            It&apos;s important to recognize when gambling might be becoming a problem
          </p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="text-left">
              <h3 className="text-lg font-semibold mb-4">Behavioral Warning Signs</h3>
              <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <li>Spending more time and money gambling than intended</li>
                <li>Lying about gambling activities to family and friends</li>
                <li>Chasing losses with bigger bets</li>
                <li>Neglecting work, family, or social responsibilities</li>
                <li>Borrowing money or selling possessions to fund gambling</li>
                <li>Feeling restless or irritable when trying to cut down</li>
                <li>Using gambling as an escape from problems or negative feelings</li>
              </ul>
            </div>

            <div className="text-left">
              <h3 className="text-lg font-semibold mb-4">Emotional Warning Signs</h3>
              <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <li>Feeling anxious, depressed, or guilty about gambling</li>
                <li>Mood swings related to wins and losses</li>
                <li>Preoccupation with gambling and planning the next session</li>
                <li>Feeling like you need to gamble with increasing amounts</li>
                <li>Difficulty sleeping due to gambling-related stress</li>
                <li>Loss of interest in other activities and hobbies</li>
                <li>Relationship problems caused by gambling behavior</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Tips for Responsible Gaming */}
        <div className="mt-14 border-t border-gray-200 dark:border-gray-700 pt-6">
          <h2 className="text-xl sm:text-2xl font-semibold text-left">
            Tips for Responsible Gaming
          </h2>
          <div className="my-8 h-px bg-gray-200 dark:bg-gray-700" />
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 text-left">
            Follow these guidelines to ensure your gaming remains fun and safe
          </p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
            {tips.map((t) => (
              <div key={t.title} className="flex items-start gap-3">
                <div className="mt-[2px] w-7 h-7 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 flex items-center justify-center">
                  <FiCheckCircle className="w-4 h-4 text-blue-500" />
                </div>
                <div className="text-left">
                  <h4 className="text-sm font-semibold">{t.title}</h4>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Get Help & Support */}
        <div className="mt-14 border-t border-gray-200 dark:border-gray-700 pt-6">
          <h2 className="text-xl sm:text-2xl font-semibold text-left">
            Get Help &amp; Support
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 text-left">
            If you or someone you know needs help with gambling problems, these resources are available 24/7
          </p>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
            {resources.map((r) => (
              <div
                key={r.name}
                className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-5 shadow-sm text-left transition-colors duration-300"
              >
                <div className="w-8 h-8 rounded-md border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <FiLink className="w-4 h-4 text-blue-500" />
                </div>
                <h3 className="mt-3 text-sm font-semibold">{r.name}</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{r.blurb}</p>
                <div className="mt-3 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <FiPhone className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <span>{r.phone}</span>
                </div>
                <a
                  href={r.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-3 py-1.5 text-sm font-medium hover:bg-blue-100 dark:hover:bg-blue-600/20 transition no-underline"
                >
                  Visit Website <FiArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Age Verification */}
        <div className="mt-14 border-t border-gray-200 dark:border-gray-700 pt-6">
          <h2 className="text-xl sm:text-2xl font-semibold text-left">
            Age Verification &amp; Legal Compliance
          </h2>
          <div className="my-8 h-px bg-gray-200 dark:bg-gray-700" />
          <p className="mt-2 text-sm font-medium text-red-600 dark:text-red-400 text-left">
            You must be 18 years or older to participate in gambling activities.
          </p>
          <p className="mt-4 text-sm text-gray-600 dark:text-gray-400 max-w-[680px] text-left">
            As affiliates, we stress the need for age verification in gaming. We partner with platforms using advanced technology to prevent underage gambling, ensuring all players submit valid ID to confirm their age before playing.
          </p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="text-left">
              <h3 className="text-sm font-semibold mb-1">Licensed &amp; Regulated</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                We only list strictly licensed and fully regulated casinos for your peace of mind.
              </p>
            </div>
            <div className="text-left">
              <h3 className="text-sm font-semibold mb-1">Secure &amp; Fair</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                All slot games are tested for fairness and randomness.
              </p>
            </div>
            <div className="text-left">
              <h3 className="text-sm font-semibold mb-1">Data Protection</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Ensure your personal information is protected with industry-leading security.
              </p>
            </div>
          </div>
        </div>

        <div className="my-8 h-px bg-gray-200 dark:bg-gray-700" />
      </div>
    </section>
  );
};

export default ResponsibleGambling;
